/**
 * Top-level content loader — `KueezSDK.loader`.
 *
 * - Off for auto-bind until `configure()` or `data-kz-loader`.
 * - No `container` → fixed full-screen overlay (centered text / custom content).
 * - With `container` → in-place UI (skeleton by default, or custom `content`).
 * - `show` / `hide` are refcounted so overlapping fetches do not flicker.
 * - Manual `show` / `hide` / `toggle` work even when auto-bind is off.
 */

import { warn } from '../util/log';

const STYLE_ID = 'kz-sdk-loader-style';
const OVERLAY_ID = 'kz-sdk-loader';
const SKELETON_CLASS = 'kz-sdk-skeleton';
const OVERLAY_CLASS = 'kz-sdk-loader-overlay';
/** Marks an in-place host owned by the loader when custom `content` is used (no skeleton chrome). */
const INPLACE_ATTR = 'data-kz-loader-inplace';

const STYLE = `
.${OVERLAY_CLASS} {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
  box-sizing: border-box;
  padding: 24px;
}
.${OVERLAY_CLASS} .kz-sdk-loader-panel {
  background: #fff;
  color: #0f172a;
  border-radius: 12px;
  padding: 20px 28px;
  font: 500 15px/1.4 system-ui, -apple-system, sans-serif;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  max-width: min(420px, 100%);
  text-align: center;
}
@media (prefers-color-scheme: dark) {
  .${OVERLAY_CLASS} .kz-sdk-loader-panel {
    background: #1e293b;
    color: #f8fafc;
  }
}
.${SKELETON_CLASS} {
  display: block;
  padding: 16px 0;
  width: 100%;
  box-sizing: border-box;
  --kz-skel-bg: #e5e7eb;
  --kz-skel-hl: #f3f4f6;
}
@media (prefers-color-scheme: dark) {
  .${SKELETON_CLASS} {
    --kz-skel-bg: #2a2d33;
    --kz-skel-hl: #383b42;
  }
}
.${SKELETON_CLASS} .kz-skel-bar,
.${SKELETON_CLASS} .kz-skel-block {
  background: linear-gradient(
    90deg,
    var(--kz-skel-bg) 0%,
    var(--kz-skel-hl) 50%,
    var(--kz-skel-bg) 100%
  );
  background-size: 200% 100%;
  border-radius: 8px;
  animation: kz-skel-shimmer 1.4s linear infinite;
}
.${SKELETON_CLASS} .kz-skel-bar {
  height: 14px;
  margin: 8px 0;
  width: 100%;
}
.${SKELETON_CLASS} .kz-skel-bar.kz-skel-short {
  width: 60%;
}
.${SKELETON_CLASS} .kz-skel-block {
  height: 160px;
  margin: 16px 0;
  border-radius: 12px;
}
@keyframes kz-skel-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
@media (prefers-reduced-motion: reduce) {
  .${SKELETON_CLASS} .kz-skel-bar,
  .${SKELETON_CLASS} .kz-skel-block {
    animation: none;
  }
}
`;

export interface LoaderConfigureOptions {
  /** Element, CSS selector, or omit/null for full-screen overlay. */
  container?: HTMLElement | string | null;
  /** Overlay label when `content` is absent. Default: `Loading...`. */
  text?: string;
  /** Custom UI; wins over text (overlay) and skeleton (in-place). */
  content?: HTMLElement | string;
}

let stylesInjected = false;
let autoBindEnabled = false;
let depth = 0;
let options: LoaderConfigureOptions = {};
/** In-place host element currently marked as skeleton, if any. */
let activeInPlace: HTMLElement | null = null;

function ensureStyles(): void {
  if (stylesInjected) return;
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) {
    stylesInjected = true;
    return;
  }
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = STYLE;
  (document.head ?? document.documentElement).appendChild(el);
  stylesInjected = true;
}

function resolveContainer(
  value: HTMLElement | string | null | undefined,
): HTMLElement | null {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value !== 'string') return value;
  if (typeof document === 'undefined') return null;
  const el = document.querySelector(value);
  return el instanceof HTMLElement ? el : null;
}

function defaultSkeleton(lines = 4, blocks = 0): string {
  const safeLines = Math.max(0, Math.floor(lines));
  const safeBlocks = Math.max(0, Math.floor(blocks));
  const parts: string[] = [];
  for (let i = 0; i < safeBlocks; i++) {
    parts.push('<div class="kz-skel-block" aria-hidden="true"></div>');
  }
  for (let i = 0; i < safeLines; i++) {
    const last = i === safeLines - 1 && safeLines > 1;
    parts.push(
      `<div class="kz-skel-bar${last ? ' kz-skel-short' : ''}" aria-hidden="true"></div>`,
    );
  }
  return parts.join('');
}

function fillCustomContent(host: HTMLElement, content: HTMLElement | string): void {
  host.innerHTML = '';
  if (typeof content === 'string') {
    host.innerHTML = content;
    return;
  }
  host.appendChild(content.cloneNode(true));
}

function renderOverlay(): void {
  if (typeof document === 'undefined') return;
  ensureStyles();
  let overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.className = OVERLAY_CLASS;
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.setAttribute('aria-busy', 'true');
    (document.body ?? document.documentElement).appendChild(overlay);
  }
  const panel = document.createElement('div');
  panel.className = 'kz-sdk-loader-panel';
  if (options.content !== undefined) {
    fillCustomContent(panel, options.content);
  } else {
    panel.textContent = (options.text ?? 'Loading...').trim() || 'Loading...';
  }
  overlay.innerHTML = '';
  overlay.appendChild(panel);
}

function ownsInPlace(el: HTMLElement): boolean {
  return el.classList.contains(SKELETON_CLASS) || el.hasAttribute(INPLACE_ATTR);
}

function clearInPlace(el: HTMLElement): void {
  el.classList.remove(SKELETON_CLASS);
  el.removeAttribute(INPLACE_ATTR);
  el.removeAttribute('aria-busy');
  el.innerHTML = '';
}

function renderInPlace(container: HTMLElement): void {
  ensureStyles();
  activeInPlace = container;
  container.setAttribute('aria-busy', 'true');
  if (options.content !== undefined) {
    container.setAttribute(INPLACE_ATTR, '');
    container.classList.remove(SKELETON_CLASS);
    fillCustomContent(container, options.content);
  } else {
    container.removeAttribute(INPLACE_ATTR);
    container.classList.add(SKELETON_CLASS);
    container.innerHTML = defaultSkeleton();
  }
}

function unrender(): void {
  if (typeof document !== 'undefined') {
    const overlay = document.getElementById(OVERLAY_ID);
    if (overlay) overlay.remove();
  }
  if (activeInPlace) {
    if (ownsInPlace(activeInPlace)) clearInPlace(activeInPlace);
    activeInPlace = null;
  }
}

function render(): void {
  const container = resolveContainer(options.container);
  if (container) {
    const overlay = typeof document !== 'undefined' ? document.getElementById(OVERLAY_ID) : null;
    if (overlay) overlay.remove();
    renderInPlace(container);
    return;
  }
  if (activeInPlace) {
    if (ownsInPlace(activeInPlace)) clearInPlace(activeInPlace);
    activeInPlace = null;
  }
  renderOverlay();
}

/** Merge options and enable auto-bind on pool content fetches. */
export function configure(opts: LoaderConfigureOptions = {}): void {
  options = { ...options, ...opts };
  autoBindEnabled = true;
}

/** True when `configure()` / `data-kz-loader` enabled auto-bind. */
export function isAutoBindEnabled(): boolean {
  return autoBindEnabled;
}

export function isActive(): boolean {
  return depth > 0;
}

export function show(): void {
  depth += 1;
  if (depth === 1) render();
}

export function hide(): void {
  if (depth <= 0) return;
  depth -= 1;
  if (depth === 0) unrender();
}

/** Omit arg to flip; pass true/false to force show/hide. */
export function toggle(active?: boolean): void {
  const want = active === undefined ? !isActive() : active;
  if (want) {
    if (!isActive()) show();
    return;
  }
  while (depth > 0) hide();
}

/** Pool fetches call this when auto-bind is enabled. */
export function showForFetch(): void {
  if (!autoBindEnabled) return;
  show();
}

/** Pool fetches call this when auto-bind is enabled. */
export function hideForFetch(): void {
  if (!autoBindEnabled) return;
  hide();
}

const PATH_RE = /^[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*$/;

function resolveWindowPath(path: string): unknown {
  let cursor: unknown = window;
  for (const segment of path.split('.')) {
    if (cursor === null || cursor === undefined) return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

/**
 * Apply `data-kz-loader` from the script tag.
 * Empty / true → defaults; dotted path → options object on `window`.
 */
export function applyLoaderScriptAttr(raw: string | undefined): void {
  if (raw === undefined) return;
  const trimmed = raw.trim();
  const asBool =
    !trimmed || trimmed === '1' || trimmed.toLowerCase() === 'true' || trimmed.toLowerCase() === 'yes'
      ? true
      : trimmed === '0' || trimmed.toLowerCase() === 'false' || trimmed.toLowerCase() === 'no'
        ? false
        : undefined;

  if (asBool === false) return;
  if (asBool === true) {
    configure({});
    return;
  }

  if (!PATH_RE.test(trimmed)) {
    warn(`[kueez-sdk/loader] Invalid data-kz-loader path: ${trimmed}`);
    return;
  }
  const value = resolveWindowPath(trimmed);
  if (value === null || value === undefined || typeof value !== 'object') {
    warn(`[kueez-sdk/loader] data-kz-loader config not found: ${trimmed}`);
    configure({});
    return;
  }
  configure(value as LoaderConfigureOptions);
}

export const loaderApi = {
  configure,
  show,
  hide,
  toggle,
  isActive,
};

/** Test-only — reset module state. */
export function _resetLoaderForTests(): void {
  autoBindEnabled = false;
  depth = 0;
  options = {};
  stylesInjected = false;
  activeInPlace = null;
  if (typeof document !== 'undefined') {
    document.getElementById(OVERLAY_ID)?.remove();
    document.getElementById(STYLE_ID)?.remove();
    document.querySelectorAll(`.${SKELETON_CLASS}, [${INPLACE_ATTR}]`).forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.classList.remove(SKELETON_CLASS);
      el.removeAttribute(INPLACE_ATTR);
      el.removeAttribute('aria-busy');
    });
  }
}
